const TEMPLATE_META = {
  'blank-project': {
    id: 'blank',
    icon: '📄',
    order: 0,
    name: {
      en: 'Blank Project',
      nb: 'Tomt prosjekt'
    },
    description: {
      en: 'Start from scratch with an empty canvas',
      nb: 'Start fra bunnen av med et tomt lerret'
    }
  },
  'sample-project': {
    id: 'software',
    icon: '💻',
    order: 1,
    name: {
      en: 'Software Development',
      nb: 'Programvareutvikling'
    },
    description: {
      en: 'Digital transformation with frontend, backend, data, and QA tracks',
      nb: 'Digital transformasjon med spor for frontend, backend, data og kvalitet'
    }
  },
  'home-renovation': {
    id: 'renovation',
    icon: '🏠',
    order: 2,
    name: {
      en: 'Home Renovation',
      nb: 'Boligoppussing'
    },
    description: {
      en: 'Fictional mid-progress whole-home upgrade with coordinated trades',
      nb: 'Fiktivt boligprosjekt midt i gjennomforingen med samordnede fag'
    }
  }
}

const templateModules = import.meta.glob('./*.json', {
  eager: true,
  import: 'default'
})

function parseTemplateFile(filePath) {
  const fileName = filePath.split('/').pop()?.replace('.json', '')
  if (!fileName) return null

  const localeMatch = fileName.match(/-(en|nb)$/)
  const locale = localeMatch?.[1] || null
  const key = locale ? fileName.slice(0, -(locale.length + 1)) : fileName

  return {
    key,
    locale
  }
}

function buildTemplateCatalog() {
  const groupedTemplates = new Map()

  for (const [path, data] of Object.entries(templateModules)) {
    const parsed = parseTemplateFile(path)
    if (!parsed) continue

    const existing = groupedTemplates.get(parsed.key) || {
      key: parsed.key,
      localeData: {},
      defaultData: null
    }

    if (parsed.locale) {
      existing.localeData[parsed.locale] = data
    } else {
      existing.defaultData = data
    }

    groupedTemplates.set(parsed.key, existing)
  }

  return Array.from(groupedTemplates.values()).map((template) => {
    const meta = TEMPLATE_META[template.key] || {}
    return {
      key: template.key,
      id: meta.id || template.key,
      icon: meta.icon || '🧩',
      order: meta.order ?? 999,
      name: meta.name || null,
      description: meta.description || null,
      data: {
        ...template.localeData,
        default: template.defaultData
      }
    }
  })
}

const templateCatalog = buildTemplateCatalog()

function resolveLanguage(language) {
  return language === 'nb' ? 'nb' : 'en'
}

function getLocalizedValue(value, language) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value[resolveLanguage(language)] ?? value.en
  }

  return value
}

function resolveTemplateData(example, language) {
  const localizedLanguage = resolveLanguage(language)
  return example.data[localizedLanguage] || example.data.en || example.data.nb || example.data.default || null
}

function getExampleRecord(example, language) {
  const data = resolveTemplateData(example, language)
  const fallbackName = data?.name || example.id
  const fallbackDescription = data?.description || ''

  return {
    id: example.id,
    name: getLocalizedValue(example.name, language) || fallbackName,
    description: getLocalizedValue(example.description, language) || fallbackDescription,
    icon: example.icon,
    data
  }
}

export function getExamples(language = 'en') {
  return templateCatalog
    .map(example => getExampleRecord(example, language))
    .filter(example => example.data)
    .sort((a, b) => {
      const aOrder = templateCatalog.find(t => t.id === a.id)?.order ?? 999
      const bOrder = templateCatalog.find(t => t.id === b.id)?.order ?? 999
      if (aOrder !== bOrder) return aOrder - bOrder
      return a.name.localeCompare(b.name)
    })
}

export function getExample(id, language = 'en') {
  const example = templateCatalog.find(ex => ex.id === id)
  return example ? getExampleRecord(example, language) : null
}

export function getExampleData(id, language = 'en') {
  const example = getExample(id, language)
  return example ? JSON.parse(JSON.stringify(example.data)) : null
}
